import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { AllLabels } from '../../constants/app.constants';
import { UserLoginResponse } from '../../domains/common.domain';
import { CommunicationService } from '../../services/communication.service';
import { ErrorService } from '../../services/error/error.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { UserService } from '../../services/user.service';
import { Router } from '@angular/router';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ValidationsService } from 'src/app/services/validations.service';

@Component({
  selector: 'app-login',
  styleUrls: ['./login.component.scss'],
  templateUrl: './login.component.html',
})
export class LoginComponent extends DestroyableComponent implements OnChanges {
  @Input() email: string = '';
  @Output() changeView: EventEmitter<{ id: number; email?: string; reason?: string }> = new EventEmitter<{ id: number; email?: string }>();

  appLabels: {
    [key: string]: string;
  } = AllLabels.app;

  isLoading: boolean;
  showPassword: boolean;

  errorMessage?: string;

  userFormGroup: FormGroup;

  constructor(
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private route: Router
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.prepareForm();
  }

  submitFormData(): void {
    if (!this.userFormGroup.valid) {
      return;
    }

    const { email, password } = this.userFormGroup.value;
    const requestData = {
      login: [
        {
          email,
        },
        {
          password,
        },
      ],
    };

    this.isLoading = true;
    this.preventLeak(this.communicationService.loginPostData<UserLoginResponse>('login', requestData)).subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, false);

        const result = response.body.login?.find(() => true);

        if (result?.result.toLowerCase() !== 'success') {
          this.errorMessage = 'app.user-form.login-failed';
          return;
        }

        const technicianId = result.technician_id;
        const technician = result.technician?.find(() => true);

        if (technicianId && technician) {
          const { role, status } = technician;
          const userData = {
            status: status,
            technician_id: technicianId,
            user_email: email,
            role: role,
          };

          this.localStorageService.setItem('technician_id', technicianId);
          this.localStorageService.setItem('user_email', email);
          this.localStorageService.setItem('bearer', response.headers.get('bearer'));

          this.userService.setUserData(userData, role, status);

          if (technician.role === 'customer') {
            this.route.navigate(['/panel']);
          }
        }
      },
      (error: HttpErrorResponse) => this.handleErrorResponse(error, email),
      () => (this.isLoading = false)
    );
  }

  showRegistrationForm(): void {
    this.changeView.emit({ id: 1 });
  }

  showResetForm(): void {
    this.changeView.emit({ id: 2 });
  }

  closeDialog() {
    this.communicationService.dashboardLogoClick(false);
  }

  private prepareForm(): void {
    this.userFormGroup = new FormGroup({
      email: new FormControl(this.email || '', [ValidationsService.emailValidator(), Validators.required]),
      password: new FormControl('', [Validators.required]),
    });

    this.preventLeak(this.userFormGroup.valueChanges).subscribe(() => (this.errorMessage = null));
  }

  private handleErrorResponse(error: HttpErrorResponse, email: string) {
    const result = error?.error?.result?.toLowerCase();
    if (result === 'password_reset' || result === 'password_expired') {
      this.changeView.emit({ id: 2, email, reason: 'app.user-form.password-expired' });
      return;
    }

    if (error.status === 401) {
      this.errorMessage = 'app.user-form.login-failed';
      return;
    }

    if (error.status === 404) {
      this.errorMessage = 'app.user-form.invalid-user';
    }
  }
}
